import type { StudioProjectOutputKind } from "@/features/project/lib/studio-project-plan";
import type { IdeSelectionContext, IdeSelectionKind } from "../selection-context";
import type {
  StudioAuthorityDomain,
  StudioAuthorityTask,
} from "../studio-authority-workspaces";

/**
 * Pure resolver for the Studio Phase 3 contextual task menu.
 *
 * No React, no store — this module ranks the existing StudioAuthorityTask
 * catalog into a small set of selection-aware "primary" tasks plus an
 * exhaustive "More" overflow, and derives a single recommendation. It never
 * executes, approves, or opens Chat: it only produces data for a caller to
 * render and act on.
 */

/**
 * The `publication` variant was removed on 2026-08-19 with its only producer:
 * `deriveContextualBlocker` built it from a cockpit activity `source:
 * "publish"` that `selectStudioCockpitActivities` never emitted. Do not
 * restore it off `evaluateStudioPublishReadiness` — that gate reports
 * READINESS (`publishReady: false` is the normal state of an unfinished
 * project), not a refused publish attempt. See `deriveContextualBlocker`'s
 * comment in `use-studio-contextual-tasks.ts` for the full measurement.
 */
export type StudioContextualBlocker = {
  kind: "validation";
  message: string;
  taskId: string;
};

export type StudioContextualTaskDisabledReason =
  | "target-unavailable"
  | "authority-unavailable"
  | null;

export interface StudioContextualTaskItem {
  task: StudioAuthorityTask;
  disabled: boolean;
  disabledReason: StudioContextualTaskDisabledReason;
}

export type StudioContextualRecommendationReason =
  | "resolve-blocker"
  | "edit-element"
  | "edit-block"
  | "edit-page"
  | "start-overview"
  | "review-release"
  | null;

export interface StudioContextualTaskMenu {
  primaryTasks: StudioContextualTaskItem[];
  moreTasks: StudioContextualTaskItem[];
  recommendedTaskId: string | null;
  recommendationReason: StudioContextualRecommendationReason;
}

export interface ResolveStudioContextualTaskMenuInput {
  domain: StudioAuthorityDomain;
  outputKind: StudioProjectOutputKind;
  tasks: readonly StudioAuthorityTask[];
  selection: IdeSelectionContext | null;
  blocker: StudioContextualBlocker | null;
  publishReady: boolean;
  targetAvailable: boolean;
}

const PRIMARY_MENU_MIN = 3;
const PRIMARY_MENU_MAX = 5;

/** Selection-kind key used to look up a fixed intent order. `"none"` covers a null selection. */
type SelectionIntentKey = IdeSelectionKind | "none";

type IntentOrderTable = Record<SelectionIntentKey, readonly string[]>;

/**
 * Fixed intent orders keyed by output kind and selection kind. Each list is a
 * priority order of StudioAuthorityTask ids; ids absent from the supplied
 * catalog are filtered out before use, so these tables may reference ids
 * that don't exist for every domain without breaking anything.
 */
const SELECTION_INTENT_ORDER: Record<StudioProjectOutputKind, IntentOrderTable> = {
  app: {
    // `publish` stays on the primary creator rail. Hiding it under More left
    // no cue to the Publish surface (TopBar blocked-publish comment; live DGX
    // walk). Append, do not prepend: recommendation still starts at overview.
    none: ["overview", "structure", "data", "publish"],
    "preview-property": ["overview", "structure", "data", "publish"],
    page: ["structure", "overview", "data", "publish"],
    block: ["structure", "behavior", "data", "overview", "publish"],
    element: ["structure", "behavior", "data", "overview", "publish"],
  },
  document: {
    none: ["overview", "structure", "sources"],
    "preview-property": ["overview", "structure", "sources"],
    page: ["structure", "overview", "sources"],
    block: ["structure", "content", "sources", "overview"],
    element: ["structure", "content", "sources", "overview"],
  },
  game: {
    none: ["overview", "scenes", "data"],
    "preview-property": ["overview", "scenes", "data"],
    page: ["scenes", "overview", "data"],
    block: ["scenes", "rules", "data", "overview"],
    element: ["scenes", "rules", "data", "overview"],
  },
};

/** Promoted order when the release is publish-ready — takes priority over selection intent. */
const PUBLISH_READY_ORDER: Record<StudioProjectOutputKind, readonly string[]> = {
  app: ["overview", "test", "publish", "launch"],
  document: ["overview", "sources", "export", "structure"],
  game: ["overview", "playtest", "publish", "launch"],
};

/** Task ids that operate on the current selection target rather than the whole project. */
const TARGET_DEPENDENT_TASK_IDS: Record<StudioProjectOutputKind, readonly string[]> = {
  app: ["structure", "behavior", "data"],
  document: ["structure", "content", "sources"],
  game: ["scenes", "rules", "data"],
};

function dedupe(ids: readonly string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const id of ids) {
    if (seen.has(id)) continue;
    seen.add(id);
    result.push(id);
  }
  return result;
}

function computeBaseOrder(
  input: ResolveStudioContextualTaskMenuInput,
  catalogIds: readonly string[],
): readonly string[] {
  if (input.domain !== "primary") {
    // Non-primary domains (design/guide/insights) don't have a curated
    // intent table; fall back to the catalog's own authored order.
    return catalogIds;
  }
  if (input.publishReady) {
    return PUBLISH_READY_ORDER[input.outputKind];
  }
  const selectionKey: SelectionIntentKey = input.selection?.kind ?? "none";
  const table = SELECTION_INTENT_ORDER[input.outputKind];
  return table[selectionKey] ?? table.none;
}

function buildPrimaryIds(
  input: ResolveStudioContextualTaskMenuInput,
  catalogIds: readonly string[],
  taskById: ReadonlyMap<string, StudioAuthorityTask>,
): { ids: string[]; blockerApplied: boolean } {
  const base = computeBaseOrder(input, catalogIds);
  let ids = dedupe(base.filter((id) => taskById.has(id)));

  const blockerApplied = Boolean(input.blocker && taskById.has(input.blocker.taskId));
  if (blockerApplied) {
    ids = dedupe([input.blocker!.taskId, ...ids]);
  }

  ids = ids.slice(0, PRIMARY_MENU_MAX);

  const fillTarget = Math.min(PRIMARY_MENU_MIN, catalogIds.length);
  if (ids.length < fillTarget) {
    const present = new Set(ids);
    for (const id of catalogIds) {
      if (ids.length >= fillTarget) break;
      if (present.has(id)) continue;
      ids.push(id);
      present.add(id);
    }
  }

  return { ids, blockerApplied };
}

function computeDisabledState(
  taskId: string,
  input: ResolveStudioContextualTaskMenuInput,
): { disabled: boolean; disabledReason: StudioContextualTaskDisabledReason } {
  if (input.domain === "primary" && input.selection && !input.targetAvailable) {
    const dependentIds = TARGET_DEPENDENT_TASK_IDS[input.outputKind] ?? [];
    if (dependentIds.includes(taskId)) {
      return { disabled: true, disabledReason: "target-unavailable" };
    }
  }
  return { disabled: false, disabledReason: null };
}

function buildItem(
  taskId: string,
  input: ResolveStudioContextualTaskMenuInput,
  taskById: ReadonlyMap<string, StudioAuthorityTask>,
): StudioContextualTaskItem {
  const task = taskById.get(taskId)!;
  const { disabled, disabledReason } = computeDisabledState(taskId, input);
  return { task, disabled, disabledReason };
}

function computeRecommendation(
  input: ResolveStudioContextualTaskMenuInput,
  primaryTasks: readonly StudioContextualTaskItem[],
  blockerApplied: boolean,
): { recommendedTaskId: string | null; recommendationReason: StudioContextualRecommendationReason } {
  // A recommendation must be executable: never point the creator at a task
  // that is rendered disabled (e.g. its target went stale). The disabled item
  // keeps its position and its reason; only the recommendation moves on.
  const recommended = primaryTasks.find((item) => !item.disabled);
  if (!recommended) {
    return { recommendedTaskId: null, recommendationReason: null };
  }

  const recommendedTaskId = recommended.task.id;

  if (blockerApplied && input.blocker?.taskId === recommendedTaskId) {
    return { recommendedTaskId, recommendationReason: "resolve-blocker" };
  }
  if (input.domain === "primary" && input.publishReady) {
    return { recommendedTaskId, recommendationReason: "review-release" };
  }
  if (!input.selection) {
    return { recommendedTaskId, recommendationReason: "start-overview" };
  }
  switch (input.selection.kind) {
    case "element":
      return { recommendedTaskId, recommendationReason: "edit-element" };
    case "block":
      return { recommendedTaskId, recommendationReason: "edit-block" };
    case "page":
      return { recommendedTaskId, recommendationReason: "edit-page" };
    default:
      return { recommendedTaskId, recommendationReason: "start-overview" };
  }
}

export function resolveStudioContextualTaskMenu(
  input: ResolveStudioContextualTaskMenuInput,
): StudioContextualTaskMenu {
  const catalogIds = input.tasks.map((task) => task.id);
  if (catalogIds.length === 0) {
    return {
      primaryTasks: [],
      moreTasks: [],
      recommendedTaskId: null,
      recommendationReason: null,
    };
  }

  const taskById = new Map(input.tasks.map((task) => [task.id, task] as const));
  const { ids: primaryIds, blockerApplied } = buildPrimaryIds(input, catalogIds, taskById);
  const primaryIdSet = new Set(primaryIds);
  const moreIds = catalogIds.filter((id) => !primaryIdSet.has(id));

  const primaryTasks = primaryIds.map((id) => buildItem(id, input, taskById));
  const moreTasks = moreIds.map((id) => buildItem(id, input, taskById));

  const { recommendedTaskId, recommendationReason } = computeRecommendation(
    input,
    primaryTasks,
    blockerApplied,
  );

  return { primaryTasks, moreTasks, recommendedTaskId, recommendationReason };
}
